import {lazy, Suspense, useContext, useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Button, {atomButtonColors, atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {useMountedState, useOnScreen} from '@s-ui/react-hooks'
import BannerInfo from '@adv-mt/mt-banner-info'
import LeadPhoneCall, {leadPhoneCallTarget} from '@adv-mt/mt-lead-phone-call'
import IconMail from '@adv-mt/mt-svg-icons/lib/MailOutline'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {
  CLASS_LEAD_CONTACT_BUTTONS_INNER,
  CLASS_LEAD_CONTACT_BUTTONS_ONLY_STICKY,
  CLASS_LEAD_CONTACT_BUTTONS_STICKY,
  CLASS_LEAD_CONTACT_BUTTONS_WRAPPER,
  DRAWER_CONTENT_TYPE,
  RESERVATION_EVENTS_TRACKING
} from './config.js'

const LazyDrawerReservationInitialization = lazy(() =>
  import(/* webpackChunkName: "LazyDrawerReservationInitialization" */ '@adv-mt/mt-drawer-reservation-initialization')
)

// When it's not aligned to the top of the element, it is aligned to the bottom.
const scrollParams = {behavior: 'smooth', block: 'center'}
const BUTTON_SIZE = atomButtonSizes.LARGE

const LeadContactButtons = ({
  ad,
  adId,
  buttonWhatsapp = null,
  contactFormRef,
  contractId,
  dealer,
  financingData,
  hasCriteoEvents = false,
  hasFinancingData = false,
  // ff_web_lead_experience_whatsapp_label_buttons
  hasPhoneCallLabel = false,
  hidePhone = false,
  isLoggedUser = false,
  showContactFormAsModal = false,
  showCanariasBannerInfo: showCanariasBannerInfoProp = false,
  leadData,
  multiprovince = '',
  offerTypeId,
  onClickOpenModal = () => {},
  onLeadPhoneCall = () => {},
  onBeforePhoneClick = null,
  onlySticky = false,
  pageName,
  phoneCallTarget,
  phoneNumber,
  stickyRef
}) => {
  const [isLogged, setIsLogged] = useState(isLoggedUser)
  const [isReservationDrawerOpen, setIsReservationDrawerOpen] = useState(false)
  const [drawerContent, setDrawerContent] = useState(DRAWER_CONTENT_TYPE.RESERVATION_INFO)

  const getMountedState = useMountedState()
  const isMounted = getMountedState()

  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')

  const hasAdId = Boolean(adId)
  const {track: analyticsTrack} = useAnalyticsTrack()

  const hasButtonWhatsapp = Boolean(buttonWhatsapp)

  const classNameButtonMail = `${CLASS_LEAD_CONTACT_BUTTONS_INNER}ButtonMail`
  // ff_web_lead_experience_whatsapp_label_buttons
  const classNameButtonPhone = cx(`${CLASS_LEAD_CONTACT_BUTTONS_INNER}ButtonPhone`, {
    [`${CLASS_LEAD_CONTACT_BUTTONS_INNER}ButtonPhone--hideText`]: hasButtonWhatsapp && !hasPhoneCallLabel
  })

  const classNameButtonWhatsapp = `${CLASS_LEAD_CONTACT_BUTTONS_INNER}ButtonWhatsapp`

  const contactButtonsRef = useRef()
  const [isOnScreen] = useOnScreen({
    initialValue: true,
    once: false,
    ref: stickyRef || contactButtonsRef
  })

  const [isFormOnScreen] = useOnScreen({
    ref: contactFormRef,
    once: false
  })

  useEffect(() => {
    setIsLogged(isLoggedUser)
  }, [isLoggedUser])

  const classNames = cx(CLASS_LEAD_CONTACT_BUTTONS_WRAPPER, {
    [CLASS_LEAD_CONTACT_BUTTONS_ONLY_STICKY]: onlySticky,
    [CLASS_LEAD_CONTACT_BUTTONS_STICKY]: !isOnScreen && !isFormOnScreen
  })

  const showCanariasBannerInfo = showCanariasBannerInfoProp && !isOnScreen && !isFormOnScreen

  // ff_web_lead_experience_whatsapp_label_buttons — the phone CTA grows to host its label
  const classNameWhatsappLayout =
    hasPhoneCallLabel && !hidePhone
      ? `${CLASS_LEAD_CONTACT_BUTTONS_INNER}--whatsappLayoutWithPhoneLabel`
      : `${CLASS_LEAD_CONTACT_BUTTONS_INNER}--whatsappLayout`

  const innerClassNames = cx(
    CLASS_LEAD_CONTACT_BUTTONS_INNER,
    `${CLASS_LEAD_CONTACT_BUTTONS_INNER}--wrapper`,
    hasButtonWhatsapp ? classNameWhatsappLayout : !hidePhone && `${CLASS_LEAD_CONTACT_BUTTONS_INNER}--twoColumns`
  )

  const defaultPhoneCallTarget = hasAdId ? leadPhoneCallTarget.vehicle : leadPhoneCallTarget.dealer

  const handleScrollToContactForm = ({event}) => {
    /* we still need to access the element via getElementById
    because using ref.current.scrollIntoView doesn't work properly
    when there are elements lazy loading above the form */
    const formContactSellerId = config.get('FORM_CONTACT_SELLER_ID')
    const elementContactForm = document.getElementById(formContactSellerId)
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)

    analyticsTrack({
      event,
      data: {
        products: [
          {
            id: adId,
            category: 'ads',
            price: 0,
            quantity: 0
          }
        ]
      }
    })
  }

  const handleLeadPhoneCall = () => onLeadPhoneCall()

  const adInfo = {
    id: ad?.id,
    title: ad?.title || '',
    imageUrl: ad?.imgUrl || '',
    images: ad?.photos || [],
    price: ad?.price || null,
    instalment: financingData?.fee || null,
    dealerRating: dealer?.ratings || null,
    dealerName: dealer?.name || ''
  }

  const handleCloseReservationDrawer = () => {
    setIsReservationDrawerOpen(false)
  }

  const handleLinkInfoClick = () => {
    setIsReservationDrawerOpen(true)
    setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_TERMS)
    analyticsTrack({
      event: RESERVATION_EVENTS_TRACKING.RESERVATION_MORE_INFORMATION_CLICKED,
      data: {
        hit_origin: 'modal acceso faq',
        products: [{id: adId, category: 'ads', price: ad?.price || 0, quantity: 0}]
      }
    })
  }

  const handleLegalConditionsClick = () => {
    setIsReservationDrawerOpen(true)
    setDrawerContent(DRAWER_CONTENT_TYPE.RESERVATION_LEGAL_CONDITIONS)
  }

  const handleContactButtonClick = () => {
    showContactFormAsModal ? onClickOpenModal(true) : handleScrollToContactForm({event: 'Contact Scroll Clicked'})
  }

  return (
    <div className={classNames} ref={contactButtonsRef}>
      {/* This inner <div> will become sticky and the parent will keep a
       * height so the design doesn't break/jump when it goes sticky
       */}
      {showCanariasBannerInfo && (
        <div className={`${CLASS_LEAD_CONTACT_BUTTONS_INNER}BannerInfo`}>
          <BannerInfo />
        </div>
      )}
      <div className={innerClassNames}>
        <div className={classNameButtonMail}>
          <Button
            color={atomButtonColors.ACCENT}
            design={atomButtonDesigns.SOLID}
            size={BUTTON_SIZE}
            leftIcon={hasButtonWhatsapp ? null : <IconMail />}
            onClick={handleContactButtonClick}
            fullWidth
            shape={atomButtonShapes.CIRCULAR}
          >
            {hasButtonWhatsapp ? (
              <AtomIcon size={ATOM_ICON_SIZES.medium}>
                <IconMail />
              </AtomIcon>
            ) : (
              i18n.t('COMMON_CONTACT')
            )}
          </Button>
        </div>
        {!hidePhone && (
          <div className={classNameButtonPhone}>
            <LeadPhoneCall
              adId={adId}
              contractId={contractId}
              extendedLeadTrackingData={leadData}
              hasButtonWhatsapp={hasButtonWhatsapp}
              hasCriteoEvents={hasCriteoEvents}
              isFullWidth
              isLoggedUser={isLogged}
              multiprovince={multiprovince}
              offerTypeId={offerTypeId}
              onLeadPhoneCall={handleLeadPhoneCall}
              onBeforeClick={onBeforePhoneClick}
              pageName={pageName}
              phone={phoneNumber}
              showIcon
              size={BUTTON_SIZE}
              target={phoneCallTarget || defaultPhoneCallTarget}
              text={i18n.t('COMMON_PHONE_CALL')}
            />
          </div>
        )}
        {hasButtonWhatsapp && <div className={classNameButtonWhatsapp}>{buttonWhatsapp}</div>}
      </div>
      {isMounted && isReservationDrawerOpen && (
        <Suspense fallback={<></>}>
          <LazyDrawerReservationInitialization
            adInfo={adInfo}
            isDrawerOpen={isReservationDrawerOpen}
            onCloseDrawer={handleCloseReservationDrawer}
            drawerContent={drawerContent}
            handleLinkInfoClick={handleLinkInfoClick}
            handleLegalConditionsClick={handleLegalConditionsClick}
          />
        </Suspense>
      )}
    </div>
  )
}

LeadContactButtons.displayName = 'LeadContactButtons'
LeadContactButtons.propTypes = {
  ad: PropTypes.object,
  adId: PropTypes.number.isRequired,
  buttonWhatsapp: PropTypes.node,
  contactFormRef: PropTypes.object,
  contractId: PropTypes.number.isRequired,
  dealer: PropTypes.object,
  financingData: PropTypes.object,
  hasCriteoEvents: PropTypes.bool,
  hasFinancingData: PropTypes.bool,
  // ff_web_lead_experience_whatsapp_label_buttons
  hasPhoneCallLabel: PropTypes.bool,
  hidePhone: PropTypes.bool,
  isLoggedUser: PropTypes.bool,
  showContactFormAsModal: PropTypes.bool,
  showCanariasBannerInfo: PropTypes.bool,
  leadData: PropTypes.object,
  multiprovince: PropTypes.string,
  offerTypeId: PropTypes.number,
  onClickOpenModal: PropTypes.func,
  onLeadPhoneCall: PropTypes.func,
  onBeforePhoneClick: PropTypes.func,
  onlySticky: PropTypes.bool,
  pageName: PropTypes.string,
  phoneCallTarget: PropTypes.oneOf(Object.values(leadPhoneCallTarget)),
  phoneNumber: PropTypes.string.isRequired,
  stickyRef: PropTypes.object
}

export default LeadContactButtons
